import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../utils/api';
import { Card, PageHeader, Btn, Badge, Modal, Input } from '../components/Layout';

export default function WhatsApp() {
  const { company, mode } = useOutletContext();
  const [conversations, setConversations]   = useState([]);
  const [activeConvo,   setActiveConvo]     = useState(null);
  const [messages,      setMessages]        = useState([]);
  const [leads,         setLeads]           = useState([]);
  const [showCompose,   setShowCompose]     = useState(false);
  const [composeLead,   setComposeLead]     = useState('');
  const [composeBody,   setComposeBody]     = useState('');
  const [template,      setTemplate]        = useState('');
  const [sending,       setSending]         = useState(false);
  const [loading,       setLoading]         = useState(true);
  const [configured,    setConfigured]      = useState(true);

  useEffect(() => {
    loadConversations();
    api.get(`/leads?company=${encodeURIComponent(company)}&limit=100`)
      .then(r => setLeads(r.data.leads || []))
      .catch(() => {});
  }, [company]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/whatsapp/conversations?company=${encodeURIComponent(company)}`);
      setConversations(r.data || []);
    } catch (err) {
      if (err.response?.status === 400) setConfigured(false);
    }
    setLoading(false);
  };

  const openConversation = async (convo) => {
    setActiveConvo(convo);
    try {
      const r = await api.get(`/whatsapp/conversations/${encodeURIComponent(convo.phone)}`);
      setMessages(r.data || []);
    } catch { setMessages([]); }
  };

  const loadTemplate = async (leadId) => {
    const lead = leads.find(l => l._id === leadId);
    if (!lead) return;
    try {
      const r = await api.get(`/whatsapp/template-preview?company=${encodeURIComponent(company)}&mode=${mode}&leadName=${encodeURIComponent(lead.name)}&skill=${encodeURIComponent(lead.skill || '')}`);
      setComposeBody(r.data.message || '');
      setTemplate(r.data.message || '');
    } catch {}
  };

  const sendMessage = async () => {
    if (!composeLead || !composeBody) return alert('Select a lead and enter a message');
    const lead = leads.find(l => l._id === composeLead);
    if (!lead?.phone) return alert('Selected lead has no phone number');
    setSending(true);
    try {
      await api.post('/whatsapp/send', {
        to: lead.phone, body: composeBody,
        leadId: lead._id, leadName: lead.name, company,
      });
      setShowCompose(false);
      setComposeBody(''); setComposeLead('');
      loadConversations();
    } catch (err) {
      alert(err.response?.data?.error || 'Send failed');
    }
    setSending(false);
  };

  const timeAgo = (date) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (!configured) return (
    <div style={{ padding: 28 }}>
      <PageHeader title="📱 WhatsApp" sub="WhatsApp Business API" />
      <Card style={{ textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📱</div>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>WhatsApp Not Configured</div>
        <div style={{ color: 'var(--muted)', fontSize: 14, marginBottom: 20 }}>
          Add your WhatsApp Business API credentials to enable messaging.
        </div>
        <div style={{ background: 'var(--surface)', borderRadius: 8, padding: 16, textAlign: 'left', maxWidth: 400, margin: '0 auto', fontSize: 13, color: 'var(--mid)', fontFamily: 'monospace' }}>
          WHATSAPP_PHONE_ID=your_phone_id<br />
          WHATSAPP_TOKEN=your_access_token<br />
          WHATSAPP_VERIFY_TOKEN=any_secret
        </div>
        <div style={{ marginTop: 16, fontSize: 13, color: 'var(--muted)' }}>
          Get credentials at <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>developers.facebook.com</a>
        </div>
      </Card>
    </div>
  );

  return (
    <div style={{ padding: 28 }}>
      <PageHeader title="📱 WhatsApp" sub={`${company} — Business Messaging`}>
        <Btn primary small onClick={() => setShowCompose(true)}>+ New Message</Btn>
      </PageHeader>

      <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 180px)' }}>

        {/* Left — Conversation list */}
        <div style={{ width: 300, flexShrink: 0, background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', overflowY: 'auto' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 700, color: 'var(--muted)' }}>
            CONVERSATIONS ({conversations.length})
          </div>
          {loading ? (
            <div style={{ padding: 20, color: 'var(--muted)', fontSize: 13, textAlign: 'center' }}>Loading...</div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: 30, color: 'var(--muted)', fontSize: 13, textAlign: 'center' }}>
              No conversations yet.<br />Send your first message!
            </div>
          ) : conversations.map((c, i) => (
            <div key={i} onClick={() => openConversation(c)}
              style={{
                padding: '12px 16px', borderBottom: '1px solid var(--border)',
                cursor: 'pointer', background: activeConvo?.phone === c.phone ? 'var(--accent)11' : 'transparent',
                borderLeft: activeConvo?.phone === c.phone ? '3px solid var(--accent)' : '3px solid transparent',
              }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{c.leadName || c.phone}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)' }}>{c.lastMessageAt ? timeAgo(c.lastMessageAt) : ''}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.direction === 'inbound' ? '← ' : '→ '}{c.lastMessage}
              </div>
              {c.unread && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', marginTop: 4 }} />}
            </div>
          ))}
        </div>

        {/* Right — Messages or empty state */}
        <div style={{ flex: 1, background: 'var(--surface)', borderRadius: 10, border: '1px solid var(--border)', display: 'flex', flexDirection: 'column' }}>
          {!activeConvo ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: 'var(--muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💬</div>
              <div style={{ fontSize: 14 }}>Select a conversation to view messages</div>
              <div style={{ marginTop: 16 }}><Btn primary small onClick={() => setShowCompose(true)}>Start New Conversation</Btn></div>
            </div>
          ) : (
            <>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{activeConvo.leadName || activeConvo.phone}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>{activeConvo.phone}</div>
                </div>
                <Badge label={activeConvo.status || 'active'} color="var(--green)" />
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {messages.length === 0 ? (
                  <div style={{ color: 'var(--muted)', fontSize: 13, textAlign: 'center', marginTop: 40 }}>No messages in this conversation</div>
                ) : messages.map((msg, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: msg.direction === 'outbound' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '70%', padding: '10px 14px', borderRadius: 12,
                      background: msg.direction === 'outbound' ? 'var(--accent)' : 'var(--card)',
                      color: msg.direction === 'outbound' ? '#000' : 'var(--text)',
                      fontSize: 13, border: '1px solid var(--border)',
                    }}>
                      <div>{msg.body}</div>
                      <div style={{ fontSize: 10, marginTop: 4, opacity: 0.7, textAlign: 'right' }}>
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {msg.direction === 'outbound' && ` · ${msg.status}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {showCompose && (
        <Modal title="📱 New WhatsApp Message" onClose={() => { setShowCompose(false); setComposeBody(''); setComposeLead(''); }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Select Lead</label>
            <select value={composeLead} onChange={e => { setComposeLead(e.target.value); loadTemplate(e.target.value); }}
              style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, outline: 'none' }}>
              <option value="">-- Choose a lead --</option>
              {leads.filter(l => l.phone).map(l => (
                <option key={l._id} value={l._id}>{l.name} — {l.phone} ({l.firmName})</option>
              ))}
            </select>
            {leads.filter(l => !l.phone).length > 0 && (
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                {leads.filter(l => !l.phone).length} leads hidden (no phone number)
              </div>
            )}
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 5 }}>Message</label>
            <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)}
              rows={5} style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '10px 12px', color: 'var(--text)', fontSize: 13, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
              placeholder="Type your message..." />
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{composeBody.length} chars</div>
          </div>
          {template && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>QUICK TEMPLATE</div>
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--mid)' }}>{template}</div>
              <Btn small style={{ marginTop: 6 }} onClick={() => setComposeBody(template)}>Use Template</Btn>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Btn small onClick={() => setShowCompose(false)}>Cancel</Btn>
            <Btn primary small onClick={sendMessage} disabled={sending}>{sending ? 'Sending...' : 'Send Message'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
